import { Box, ButtonProps, CircularProgress, Button as MuiButton, css, styled } from "@mui/material";
import { HTMLAttributeAnchorTarget, MouseEvent, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { background, borderColor, button, divider, red, text } from "@/modules/irohana-components/theme/colors-new";
import { typography } from "@/modules/irohana-components/theme/typography";
import { rounded } from "@/modules/irohana-components/theme/variables";
import { FCC } from "@/modules/irohana-components/types/common";

export type TButtonNewVariant = "primary" | "secondary" | "tertiary" | "delete" | "text";
export type TButtonNewSize = "lg" | "md" | "sm" | "xs";

export type TButtonNewProps = {
  variant?: TButtonNewVariant;
  size?: TButtonNewSize;
  to?: string;
  isLoading?: boolean;
  target?: HTMLAttributeAnchorTarget;
} & Omit<ButtonProps, "variant" | "size">;

export const ButtonNew: FCC<TButtonNewProps> = ({
  variant = "primary",
  size = "md",
  fullWidth,
  children,
  isLoading: isExternalLoading,
  onClick,
  disabled,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const methods = useFormContext();

  const handleOnclick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;
    setLoading(true);
    await onClick(e);
    setLoading(false);
  };

  const isLoading = useMemo(() => {
    if (rest.type === "submit" && !!methods) return methods.formState.isSubmitting;
    return loading || isExternalLoading;
  }, [loading, methods, rest.type, isExternalLoading]);

  return (
    <StyledButton
      btnVariant={variant}
      btnSize={size}
      onClick={handleOnclick}
      sx={{ width: fullWidth ? "100%" : "fit-content" }}
      disabled={isLoading || disabled}
      {...rest}
    >
      {children}
      {isLoading && (
        <LoadingButton>
          <CircularProgress color="inherit" />
        </LoadingButton>
      )}
    </StyledButton>
  );
};

const options = { shouldForwardProp: (propName: string) => !["btnVariant", "btnSize"].includes(propName) };

const StyledButton = styled(MuiButton, options)<{ btnVariant: TButtonNewVariant; btnSize: TButtonNewSize }>`
  &.MuiButtonBase-root {
    line-height: 1;
    text-transform: none;
    position: relative;
    overflow: visible;
    color: ${text.white};
    white-space: nowrap;
    min-width: 60px;
    cursor: pointer;
    ${({ btnVariant, btnSize }) => css`
      // variants
      ${PrimaryButtonCSS}
      ${btnVariant === "secondary" && SecondaryButtonCSS};
      ${btnVariant === "tertiary" && TertiaryButtonCSS};
      ${btnVariant === "delete" && DeleteButtonCSS};
      ${btnVariant === "text" && TextButtonCSS};

      // sizes
      ${MdButtonCSS}
      ${btnSize === "lg" && LgButtonCSS}
      ${btnSize === "sm" && SmButtonCSS}
       ${btnSize === "xs" && XsButtonCSS}
    `}
    /* Focus outer border via ::after */
    position: relative;
    &::after {
      content: "";
      position: absolute;
      top: -5px;
      left: -5px;
      right: -5px;
      bottom: -5px;
      border: 2px solid transparent;
      border-radius: 8px;
      pointer-events: none;
    }
    &:focus-visible::after,
    &.Mui-focusVisible::after {
      border-color: ${borderColor.orange};
    }

    :disabled,
    &.Mui-disabled {
      background: ${button.disable};
      border: 2px solid ${button.disable};
      color: ${text.tertiary};
      &::after {
        border-color: transparent;
      }
    }
  }
`;

// Primary: filled teal, white text
const PrimaryButtonCSS = css`
  background: ${button.primary};
  border: 2px solid ${button.primary};
  color: ${text.white};
  &:focus-visible,
  &.Mui-focusVisible {
    background: ${button.primary};
    color: ${text.white};
    border: 2px solid ${button.primary};
    .MuiTouchRipple-root {
      display: none;
    }
  }
  :hover {
    background: ${button.hover};
    border-color: ${button.primary};
    color: ${button.primary};
  }
  :active {
    background: ${button.pressed};
    border-color: ${button.pressed};
    color: ${text.white};
  }
`;

// Secondary: white bg, teal border, teal text
const SecondaryButtonCSS = css`
  background: ${background.white};
  border: 2px solid ${button.primary};
  color: ${button.primary};
  &.Mui-focusVisible {
    background: ${background.white};
    color: ${button.primary};
    border: 2px solid ${button.primary};
    .MuiTouchRipple-root {
      display: none;
    }
  }
  :hover {
    background: ${button.hover};
  }
  :active {
    background: ${button.pressed};
    border-color: ${button.pressed};
    color: ${text.white};
  }
`;

// Tertiary: white bg, gray border, gray text
const TertiaryButtonCSS = css`
  background: ${background.white};
  color: ${text.secondary};
  border: 2px solid ${borderColor.higher};
  &.Mui-focusVisible {
    background: ${background.white};
    color: ${text.secondary};
    border: 2px solid ${borderColor.higher};
    .MuiTouchRipple-root {
      display: none;
    }
  }
  :hover {
    color: ${text.secondary};
    border: 2px solid ${borderColor.higher};
    background: ${background.base};
  }
  :active {
    background: ${button.disable};
    color: ${text.secondary};
  }
`;

// Delete: white bg, red border, red text
const DeleteButtonCSS = css`
  background: ${background.white};
  color: ${red[70]};
  border: 2px solid ${borderColor.error};
  &.Mui-focusVisible {
    background: ${background.white};
    color: ${red[70]};
    border: 2px solid ${borderColor.error};
    .MuiTouchRipple-root {
      display: none;
    }
  }
  :hover {
    border: 2px solid ${borderColor.error};
    background: ${button.hoverError};
    color: ${red[70]};
  }
  :active {
    background: ${button.pressedError};
    color: ${red[70]};
  }
`;

// Text: no border, secondary text color
const TextButtonCSS = css`
  background: transparent;
  color: ${text.secondary};
  border: 2px solid transparent;
  &.Mui-focusVisible {
    background: transparent;
    color: ${text.secondary};
    border: 2px solid transparent;
    .MuiTouchRipple-root {
      display: none;
    }
  }
  :hover {
    background: ${background.base};
    color: ${text.secondary};
    border: 2px solid ${borderColor.high};
  }
  :active {
    background: ${divider.high};
    color: ${text.secondary};
  }
  :disabled {
    background: transparent !important;
    border-color: transparent !important;
  }
`;

const LgButtonCSS = css`
  height: 48px;
  padding: 0 30px;
  border-radius: ${rounded.sm};
  ${typography.head20Semi}
  .MuiCircularProgress-root {
    width: 20px !important;
    height: 20px !important;
  }
`;

const MdButtonCSS = css`
  height: 40px;
  padding: 0 22px;
  border-radius: ${rounded.sm};
  ${typography.sub16Semi}
  .MuiCircularProgress-root {
    width: 16px !important;
    height: 16px !important;
  }
`;

const SmButtonCSS = css`
  height: 37px;
  font-size: 14px !important;
  font-weight: 500;
  padding: 0 14px;
  border-radius: ${rounded.sm};
  ${typography.cap12Bold}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const XsButtonCSS = css`
  height: 28px;
  padding: 0 6px;
  border-radius: ${rounded.sm};
  ${typography.cap12Bold}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const LoadingButton = styled(Box)`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  background-color: inherit;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
